import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { redoCopy } from '../api/copyApi';

const RedoCopy = ({ copyId, copyText, onRedoComplete, onClose }) => {
    const [selectedText, setSelectedText] = useState('');
    const [selectedRange, setSelectedRange] = useState({ start: 0, end: 0 });
    const [showRedoButton, setShowRedoButton] = useState(false);
    const [showPromptWindow, setShowPromptWindow] = useState(false);
    const [redoPrompt, setRedoPrompt] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
    const textRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        let selectionTimeout;

        const processSelection = () => {
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const selectedText = selection.toString().trim();
                
                // Check if selection is within our text container
                if (textRef.current && textRef.current.contains(range.commonAncestorContainer)) {
                    if (selectedText.length > 0) {
                        // Get the range relative to the text content
                        const preCaretRange = document.createRange();
                        preCaretRange.selectNodeContents(textRef.current);
                        preCaretRange.setEnd(range.startContainer, range.startOffset);
                        const start = preCaretRange.toString().length;
                        const end = start + selectedText.length;
                        
                        setSelectedText(selectedText);
                        setSelectedRange({ start, end });
                        
                        // Position the redo button near the selection
                        const rect = range.getBoundingClientRect();
                        const containerRect = containerRef.current?.getBoundingClientRect();
                        if (containerRect) {
                            setButtonPosition({
                                top: rect.bottom - containerRect.top + 10,
                                left: rect.left - containerRect.left,
                            });
                        }
                        
                        setShowRedoButton(true);
                        return;
                    }
                }
            }
            setShowRedoButton(false);
        };

        const handleSelection = () => {
            // Clear any existing timeout
            if (selectionTimeout) {
                clearTimeout(selectionTimeout);
            }
            
            // Use timeout to debounce rapid selection events and ensure selection is stable
            selectionTimeout = setTimeout(processSelection, 150);
        };

        const handleSelectionChange = () => {
            // For selectionchange event, add a shorter delay since it's more reliable
            if (selectionTimeout) {
                clearTimeout(selectionTimeout);
            }
            selectionTimeout = setTimeout(processSelection, 50);
        };

        // Detect if device supports touch
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

        // Add appropriate event listeners based on device capabilities
        if (isTouchDevice) {
            // For touch devices, use selectionchange as primary method
            document.addEventListener('selectionchange', handleSelectionChange);
            // Also listen for touchend as fallback
            document.addEventListener('touchend', handleSelection);
        }
        
        // Always add mouse/keyboard events for desktop compatibility
        document.addEventListener('mouseup', handleSelection);
        document.addEventListener('keyup', handleSelection);

        return () => {
            if (selectionTimeout) {
                clearTimeout(selectionTimeout);
            }
            document.removeEventListener('mouseup', handleSelection);
            document.removeEventListener('keyup', handleSelection);
            document.removeEventListener('touchend', handleSelection);
            document.removeEventListener('selectionchange', handleSelectionChange);
        };
    }, [copyText]);

    const handleRedoClick = () => {
        setShowPromptWindow(true);
        setShowRedoButton(false);
        // Clear the selection
        window.getSelection().removeAllRanges();
    };

    const handleSubmit = async () => {
        if (!redoPrompt.trim()) {
            setError('Please enter a redo prompt');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const result = await redoCopy(copyId, redoPrompt, selectedRange);
            if (onRedoComplete) {
                onRedoComplete(result);
            }
            setShowPromptWindow(false);
            setRedoPrompt('');
        } catch (err) {
            console.error('Error redoing copy:', err);
            setError('Failed to redo copy. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClosePromptWindow = () => {
        setShowPromptWindow(false);
        setRedoPrompt('');
        setError(null);
    };

    return (
        <Box 
            ref={containerRef} 
            sx={{ 
                position: 'relative', 
                width: '100%',
                // Ensure container doesn't interfere with selection
                touchAction: 'manipulation',
                // Prevent zoom on double tap while allowing selection
                '@media (max-width: 600px)': {
                    touchAction: 'pan-y pinch-zoom',
                }
            }}
        >
            {/* The text content with selection capability */}
            <Typography
                ref={textRef}
                sx={{ 
                    whiteSpace: 'pre-wrap',
                    userSelect: 'text',
                    cursor: 'text',
                    WebkitUserSelect: 'text', // Safari/iOS compatibility
                    MozUserSelect: 'text', // Firefox compatibility
                    msUserSelect: 'text', // IE/Edge compatibility
                    WebkitTouchCallout: 'default', // iOS long-press menu
                    WebkitTapHighlightColor: 'transparent', // Remove tap highlight
                    // Improve text selection on mobile
                    lineHeight: 1.6, // Better line spacing for touch selection
                    fontSize: { xs: '0.875rem', sm: '1rem' }, // Responsive font size
                    '&::selection': {
                        backgroundColor: 'rgba(25, 118, 210, 0.3)',
                    },
                    // Mobile-specific selection styling
                    '&::-moz-selection': {
                        backgroundColor: 'rgba(25, 118, 210, 0.3)',
                    }
                }}
            >
                {copyText || 'No copy text available.'}
            </Typography>

            {/* Redo button that appears after text selection */}
            {showRedoButton && (
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleRedoClick}
                    sx={{
                        position: 'absolute',
                        top: buttonPosition.top,
                        left: buttonPosition.left,
                        zIndex: 1000,
                        backgroundColor: 'primary.main',
                        minHeight: '44px', // Minimum touch target size for mobile
                        minWidth: '44px',
                        fontSize: '0.875rem',
                        '&:hover': {
                            backgroundColor: 'primary.dark',
                        },
                        // Ensure button is visible and accessible on mobile
                        '@media (max-width: 600px)': {
                            fontSize: '0.75rem',
                            padding: '8px 16px',
                        }
                    }}
                >
                    Redo
                </Button>
            )}

            {/* Prompt window modal */}
            {showPromptWindow && (
                <Paper
                    elevation={8}
                    sx={{
                        position: 'absolute',
                        top: buttonPosition.top + 40, // Position below where the button was
                        left: 0, // Start at container edge
                        right: 0, // End at container edge
                        width: '100%', // Full container width
                        maxWidth: 'none', // Remove max width constraint
                        p: 2,
                        zIndex: 1001,
                        backgroundColor: 'background.paper',
                        // Mobile-specific adjustments
                        '@media (max-width: 600px)': {
                            p: 2,
                            mx: 0, // Remove horizontal margin
                        },
                        // Desktop-specific constraints
                        '@media (min-width: 601px)': {
                            left: Math.max(0, buttonPosition.left - 50), // Offset from button but stay in bounds
                            right: 'auto',
                            width: 400,
                            maxWidth: '90%',
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Redo Selected Text</Typography>
                        <IconButton onClick={handleClosePromptWindow} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Selected text: "{selectedText}"
                    </Typography>
                    
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Character range: {selectedRange.start} - {selectedRange.end}
                    </Typography>

                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Redo Prompt"
                        placeholder="Enter instructions for how to redo this text..."
                        value={redoPrompt}
                        onChange={(e) => setRedoPrompt(e.target.value)}
                        sx={{ mb: 2 }}
                        disabled={loading}
                    />

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        justifyContent: 'flex-end',
                        // Stack buttons vertically on mobile for better touch targets
                        '@media (max-width: 600px)': {
                            flexDirection: 'column-reverse',
                            gap: 1,
                        }
                    }}>
                        <Button
                            variant="outlined"
                            onClick={handleClosePromptWindow}
                            disabled={loading}
                            sx={{
                                '@media (max-width: 600px)': {
                                    minHeight: '48px', // Larger touch target
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={loading || !redoPrompt.trim()}
                            startIcon={loading ? <CircularProgress size={20} /> : null}
                            sx={{
                                '@media (max-width: 600px)': {
                                    minHeight: '48px', // Larger touch target
                                }
                            }}
                        >
                            Submit
                        </Button>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default RedoCopy;
